import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertTriangle, Activity, Users, TrendingUp, TrendingDown,
  RefreshCw, Loader2, AlertCircle, Shield, Heart, Clock,
  Hospital, FileWarning, Stethoscope, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAlertColor = (level) => {
  const colors = {
    'red': 'bg-red-100 text-red-700 border-red-300',
    'orange': 'bg-orange-100 text-orange-700 border-orange-300',
    'yellow': 'bg-amber-100 text-amber-700 border-amber-300',
    'green': 'bg-emerald-100 text-emerald-700 border-emerald-300'
  };
  return colors[level] || 'bg-gray-100 text-gray-700';
};

const getRiskLevelColor = (level) => {
  const colors = {
    'critical': 'text-red-600',
    'high': 'text-orange-600',
    'moderate': 'text-amber-600',
    'low': 'text-emerald-600'
  };
  return colors[level] || 'text-gray-600';
};

export default function PredictiveOutcomes() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [earlyWarnings, setEarlyWarnings] = useState(null);
  const [declineRisk, setDeclineRisk] = useState(null);
  const [hospRisk, setHospRisk] = useState(null);
  const [dailyHuddle, setDailyHuddle] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetail, setPatientDetail] = useState(null);

  const fetchData = async (retryCount = 0) => {
    setLoading(true);
    const token = localStorage.getItem('qapi-token');
    
    if (!token && retryCount < 3) {
      setTimeout(() => fetchData(retryCount + 1), 1000);
      return;
    }
    
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [summaryRes, warningsRes, declineRes, hospRes, huddleRes] = await Promise.all([
        fetch(`${API_URL}/api/predictive-outcomes/summary`, { headers }),
        fetch(`${API_URL}/api/predictive-outcomes/early-warnings`, { headers }),
        fetch(`${API_URL}/api/predictive-outcomes/decline-risk`, { headers }),
        fetch(`${API_URL}/api/predictive-outcomes/hospitalization-risk`, { headers }),
        fetch(`${API_URL}/api/predictive-outcomes/daily-huddle`, { headers })
      ]);

      // Retry on 401
      if (summaryRes.status === 401 && retryCount < 2) {
        setTimeout(() => fetchData(retryCount + 1), 1000);
        return;
      }

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (warningsRes.ok) setEarlyWarnings(await warningsRes.json());
      if (declineRes.ok) setDeclineRisk(await declineRes.json());
      if (hospRes.ok) setHospRisk(await hospRes.json());
      if (huddleRes.ok) setDailyHuddle(await huddleRes.json());
    } catch (error) {
      console.error('Error fetching predictive data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientDetail = async (patientId) => {
    const token = localStorage.getItem('qapi-token');
    try {
      const res = await fetch(`${API_URL}/api/predictive-outcomes/patient/${patientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPatientDetail(data);
        setSelectedPatient(patientId);
      }
    } catch (error) {
      console.error('Error fetching patient detail:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading predictive analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="predictive-outcomes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Predictive Patient Outcomes</h1>
          <p className="text-muted-foreground">AI-powered early warning system for patient risk assessment</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Alert Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-red-500" data-testid="critical-alerts-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical Alerts</p>
                <p className="text-3xl font-bold text-red-600">{summary?.alert_distribution?.red || 0}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <p className="text-xs text-red-600 mt-1">Immediate review required</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500" data-testid="high-risk-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">High Risk</p>
                <p className="text-3xl font-bold text-orange-600">{summary?.alert_distribution?.orange || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-500" />
            </div>
            <p className="text-xs text-orange-600 mt-1">Review within 24h</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Moderate</p>
                <p className="text-3xl font-bold text-amber-600">{summary?.alert_distribution?.yellow || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-amber-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Routine monitoring</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Stable</p>
                <p className="text-3xl font-bold text-emerald-600">{summary?.alert_distribution?.green || 0}</p>
              </div>
              <Shield className="h-8 w-8 text-emerald-500" />
            </div>
            <p className="text-xs text-emerald-600 mt-1">Low risk</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Early Warnings</TabsTrigger>
          <TabsTrigger value="decline">Decline Risk</TabsTrigger>
          <TabsTrigger value="hospitalization">Hospitalization Risk</TabsTrigger>
          <TabsTrigger value="huddle">Daily Huddle</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Critical Alerts */}
            <Card className="border-red-200">
              <CardHeader className="bg-red-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  Critical Alerts ({earlyWarnings?.critical_alerts?.count || 0})
                </CardTitle>
                <CardDescription className="text-red-600">
                  {earlyWarnings?.critical_alerts?.action_required}
                </CardDescription>
              </CardHeader>
              <CardContent className="max-h-80 overflow-y-auto">
                <div className="space-y-3 pt-4">
                  {earlyWarnings?.critical_alerts?.patients?.map((patient, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 border rounded-lg cursor-pointer hover:bg-red-50 transition-colors"
                      onClick={() => fetchPatientDetail(patient.patient_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Heart className="h-4 w-4 text-red-500" />
                          <span className="font-medium">{patient.patient_name}</span>
                        </div>
                        <Badge className={getAlertColor('red')}>RED</Badge>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <span>Decline Risk: <strong>{patient.decline_risk_score}%</strong></span>
                        <span>Hosp Risk: <strong>{patient.hospitalization_risk_score}%</strong></span>
                      </div>
                    </div>
                  ))}
                  {(!earlyWarnings?.critical_alerts?.patients || earlyWarnings?.critical_alerts?.patients.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">No critical alerts</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* High Risk Alerts */}
            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <AlertTriangle className="h-5 w-5" />
                  High Risk Alerts ({earlyWarnings?.high_risk_alerts?.count || 0})
                </CardTitle>
                <CardDescription className="text-orange-600">
                  {earlyWarnings?.high_risk_alerts?.action_required}
                </CardDescription>
              </CardHeader>
              <CardContent className="max-h-80 overflow-y-auto">
                <div className="space-y-3 pt-4">
                  {earlyWarnings?.high_risk_alerts?.patients?.slice(0, 10).map((patient, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 border rounded-lg cursor-pointer hover:bg-orange-50 transition-colors"
                      onClick={() => fetchPatientDetail(patient.patient_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Heart className="h-4 w-4 text-orange-500" />
                          <span className="font-medium">{patient.patient_name}</span>
                        </div>
                        <Badge className={getAlertColor('orange')}>HIGH</Badge>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <span>Decline Risk: <strong>{patient.decline_risk_score}%</strong></span>
                        <span>PPS: <strong>{patient.pps_score}</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Patient Detail Modal */}
          {patientDetail && (
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Patient Detail: {patientDetail.patient_name}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setPatientDetail(null)}>
                    Close
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Decline Risk */}
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <TrendingDown className="h-4 w-4" /> Decline Risk
                    </h4>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <span>Overall Risk</span>
                        <span className={`font-bold ${getRiskLevelColor(patientDetail.decline_risk?.risk_level)}`}>
                          {patientDetail.decline_risk?.overall_risk_score}%
                        </span>
                      </div>
                      <Progress value={patientDetail.decline_risk?.overall_risk_score} className="h-2" />
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>7-day risk:</span>
                        <span>{patientDetail.decline_risk?.risk_7_days}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>14-day risk:</span>
                        <span>{patientDetail.decline_risk?.risk_14_days}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>30-day risk:</span>
                        <span>{patientDetail.decline_risk?.risk_30_days}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Hospitalization Risk */}
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Hospital className="h-4 w-4" /> Hospitalization Risk
                    </h4>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <span>Risk Score</span>
                        <span className={`font-bold ${getRiskLevelColor(patientDetail.hospitalization_risk?.risk_level)}`}>
                          {patientDetail.hospitalization_risk?.risk_score}%
                        </span>
                      </div>
                      <Progress value={patientDetail.hospitalization_risk?.risk_score} className="h-2" />
                    </div>
                    <div className="space-y-2">
                      {patientDetail.hospitalization_risk?.recommended_actions?.map((action, idx) => (
                        <p key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 shrink-0" />
                          {action}
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Interventions */}
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Stethoscope className="h-4 w-4" /> Recommended Interventions
                    </h4>
                    <div className="space-y-2">
                      {patientDetail.recommended_interventions?.map((intervention, idx) => (
                        <div key={idx} className={`p-2 rounded-lg border-l-4 ${
                          intervention.priority === 'high' ? 'border-l-red-500 bg-red-50' :
                          intervention.priority === 'medium' ? 'border-l-amber-500 bg-amber-50' :
                          'border-l-blue-500 bg-blue-50'
                        }`}>
                          <p className="text-sm font-medium">{intervention.intervention}</p>
                          <p className="text-xs text-muted-foreground">{intervention.rationale}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="decline" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Decline Risk Analysis</CardTitle>
              <CardDescription>
                Patients categorized by risk of clinical decline
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {['critical', 'high', 'moderate', 'low'].map(level => (
                  <div key={level} className="p-4 border rounded-lg">
                    <h4 className={`font-semibold capitalize ${getRiskLevelColor(level)}`}>{level}</h4>
                    <p className="text-3xl font-bold mt-1">
                      {declineRisk?.risk_distribution?.[level]?.count || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {declineRisk?.risk_distribution?.[level]?.percentage || 0}% of census
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Top At-Risk Patients</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {declineRisk?.risk_distribution?.critical?.patients?.slice(0, 6).map((patient, idx) => (
                    <div key={idx} className="p-3 border rounded-lg border-red-200 bg-red-50">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{patient.patient_name}</span>
                        <Badge variant="destructive">{patient.decline_risk_score}%</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        PPS: {patient.pps_score} | Days on service: {patient.days_on_service}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hospitalization" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hospital className="h-5 w-5" />
                Hospitalization Risk Prevention
              </CardTitle>
              <CardDescription>
                {hospRisk?.high_hospitalization_risk_count || 0} patients at high risk 
                ({hospRisk?.high_risk_percentage || 0}% of census)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-muted-foreground">Average Risk</p>
                  <p className="text-2xl font-bold">{hospRisk?.average_hospitalization_risk || 0}%</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-muted-foreground">Prevention Opportunities</p>
                  <p className="text-2xl font-bold text-amber-600">{hospRisk?.prevention_opportunities || 0}</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-muted-foreground">Est. Preventable ED Visits</p>
                  <p className="text-2xl font-bold text-emerald-600">{hospRisk?.estimated_preventable_ed_visits || 0}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Prevention Recommendations</h4>
                <div className="space-y-2">
                  {hospRisk?.recommendations?.map((rec, idx) => (
                    <div key={idx} className="p-3 bg-blue-50 rounded-lg flex items-start gap-3">
                      <ChevronRight className="h-5 w-5 text-blue-600 shrink-0" />
                      <p className="text-sm">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3">Top 10 Hospitalization Risk</h4>
                <div className="space-y-2">
                  {hospRisk?.top_10_hospitalization_risk?.map((patient, idx) => (
                    <div key={idx} className="p-3 border rounded-lg flex items-center justify-between">
                      <div>
                        <span className="font-medium">{patient.patient_name}</span>
                        <span className="text-sm text-muted-foreground ml-2">PPS: {patient.pps_score}</span>
                      </div>
                      <Badge className={patient.hospitalization_risk_score >= 50 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                        {patient.hospitalization_risk_score}% risk
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="huddle" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Daily Huddle Report - {dailyHuddle?.huddle_date}
              </CardTitle>
              <CardDescription>Prioritized patient list for clinical team discussion</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Census</p>
                  <p className="text-2xl font-bold">{dailyHuddle?.total_census || 0}</p>
                </div>
                <div className="p-4 border rounded-lg border-red-200">
                  <p className="text-sm text-red-600">Red Alerts</p>
                  <p className="text-2xl font-bold text-red-600">{dailyHuddle?.key_metrics?.red_alerts || 0}</p>
                </div>
                <div className="p-4 border rounded-lg border-orange-200">
                  <p className="text-sm text-orange-600">Orange Alerts</p>
                  <p className="text-2xl font-bold text-orange-600">{dailyHuddle?.key_metrics?.orange_alerts || 0}</p>
                </div>
                <div className="p-4 border rounded-lg border-emerald-200">
                  <p className="text-sm text-emerald-600">Stable Patients</p>
                  <p className="text-2xl font-bold text-emerald-600">{dailyHuddle?.key_metrics?.stable_patients || 0}</p>
                </div>
              </div>

              {/* Priority 1 */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge className="bg-red-100 text-red-700">Priority 1</Badge>
                  Critical - Immediate Review ({dailyHuddle?.priority_1_critical?.count || 0})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {dailyHuddle?.priority_1_critical?.patients?.map((patient, idx) => (
                    <div key={idx} className="p-3 border rounded-lg border-red-200 bg-red-50">
                      <span className="font-medium">{patient.patient_name}</span>
                      <div className="flex gap-4 mt-1 text-xs">
                        <span>Decline: {patient.decline_risk_score}%</span>
                        <span>Hosp: {patient.hospitalization_risk_score}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Priority 2 */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge className="bg-orange-100 text-orange-700">Priority 2</Badge>
                  High Risk - Proactive Intervention ({dailyHuddle?.priority_2_high_risk?.count || 0})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {dailyHuddle?.priority_2_high_risk?.patients?.map((patient, idx) => (
                    <div key={idx} className="p-3 border rounded-lg border-orange-200 bg-orange-50">
                      <span className="font-medium">{patient.patient_name}</span>
                      <div className="flex gap-4 mt-1 text-xs">
                        <span>Decline: {patient.decline_risk_score}%</span>
                        <span>PPS: {patient.pps_score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
